import { Autocomplete, Box, SxProps, TextField, Typography } from '@mui/material'
import { t } from 'i18next'
import { debounce, isEqual } from 'lodash'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { mediumDefaultPageSize } from '../../../config/defaultPagination'
import { useNotification } from '../../../store/redux/hooks'
import {
  ProjectAcronym,
  useGetAcronymsQuery,
  useLazyGetProjectByIdQuery,
} from '../../../store/redux/reducers/projectsSlice'
import { RequestProps } from '../../../store/types'
import { filterOptions } from '../../../store/utils/filterProjectsAcronymsOptions'
import { Option } from './AutocompleteInput'

type ProjectSelectorProps = {
  value: Option | null
  onChange: (option: Option | null) => void
  label?: string
  sx?: SxProps
  loggable?: boolean
  noOptionsText?: string
  disabled?: boolean
}

const ProjectSelector = ({
  value,
  onChange,
  label,
  sx,
  loggable = false,
  noOptionsText,
  disabled = false,
}: ProjectSelectorProps) => {
  const { displayNotification } = useNotification()
  const [projects, setProjects] = useState<ProjectAcronym[]>([])
  const [page, setPage] = useState(0)
  const [searchedAcronym, setSearchedAcronym] = useState('')

  const getAcronymProps = useCallback(() => {
    return {
      page: page,
      size: mediumDefaultPageSize,
      acronym: searchedAcronym,
      loggable: loggable,
    } as RequestProps
  }, [page, searchedAcronym, loggable])

  const [acronymProps, setAcronymProps] = useState<RequestProps>(getAcronymProps())
  const { data: projectsResponse } = useGetAcronymsQuery(acronymProps)

  useEffect(() => {
    if (projectsResponse) {
      setProjects(prevProjects => {
        const existingIds = new Set(prevProjects.map(p => p.id))
        const newProjects = projectsResponse.content.filter(p => !existingIds.has(p.id))
        return [...prevProjects, ...newProjects]
      })
    }
  }, [projectsResponse])

  useEffect(() => {
    setAcronymProps(getAcronymProps())
  }, [getAcronymProps, page, searchedAcronym])

  const handleFetchMoreData = () => setPage(prev => prev + 1)

  const onSelectBottomScroll = useCallback(() => {
    if (!projectsResponse?.last) {
      handleFetchMoreData()
    }
  }, [projectsResponse?.last])

  const debouncedOnSelectBottomScroll = useMemo(
    () =>
      debounce(() => {
        onSelectBottomScroll()
      }, 100),
    [onSelectBottomScroll]
  )

  const onScroll = useCallback(
    (event: React.SyntheticEvent) => {
      const node = event.currentTarget as HTMLElement
      if (node.scrollTop + node.clientHeight >= node.scrollHeight - 1) {
        debouncedOnSelectBottomScroll()
      }
    },
    [debouncedOnSelectBottomScroll]
  )

  const debouncedSetSearchedAcronym = useMemo(
    () =>
      debounce((value: string) => {
        setSearchedAcronym(value)
        setPage(0)
      }, 500),
    []
  )

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    debouncedSetSearchedAcronym(e.target.value)
  }

  const options = useMemo(() => {
    const mapped = projects.map(project => ({ label: project.acronym, value: project.id }))
    const isValueInOptions = value && !projects.some(p => p.id === value.value)
    return isValueInOptions ? [...mapped, value] : mapped
  }, [projects, value])

  const [getProjectById] = useLazyGetProjectByIdQuery()

  useEffect(() => {
    if (!value?.value) return
    if (projects.some(p => p.id === value.value)) return

    let mounted = true
    const fetchProject = async () => {
      try {
        const result = await getProjectById({ id: value.value, date: '' })
        if (!mounted) return

        if ('data' in result && result.data?.project) {
          const proj = result.data.project
          setProjects(prev => {
            if (prev.some(p => p.id === proj.id)) return prev
            return [
              ...prev,
              {
                id: proj.id,
                acronym: proj.acronym ?? '',
                name: proj.name ?? '',
                projectStatus: proj.projectStatus,
                favorite: null,
              } as ProjectAcronym,
            ]
          })
        }
      } catch (error) {
        displayNotification({ message: t('message.failedToFetchProjectDetails'), type: 'error' })
      }
    }

    fetchProject()

    return () => {
      mounted = false
    }
  }, [value, projects, getProjectById, displayNotification])

  const displayValue = useMemo(() => {
    if (!value) return null

    const found = projects.find(p => p.id === value.value)
    if (found) {
      return { label: found.acronym || '', value: found.id }
    }

    return { label: '', value: value.value }
  }, [value, projects])

  return (
    <Autocomplete
      sx={{ width: '100%', ...sx }}
      value={displayValue || null}
      onChange={(_, newValue) => onChange((newValue as Option) || null)}
      disabled={disabled}
      options={options}
      getOptionLabel={option => option.label || ''}
      noOptionsText={noOptionsText || t('page.worklog.form.projectsNotFound')}
      renderGroup={params => (
        <div key={params.key}>
          <Box sx={{ px: 1.5, py: 1 }}>
            <Typography variant="body1" sx={{ color: 'text.highEmphasis' }}>
              {params.group}
            </Typography>
          </Box>
          <Box sx={{ color: 'text.base' }}>{params.children}</Box>
        </div>
      )}
      renderInput={params => (
        <TextField {...(params as any)} onChange={handleInputChange} size={'small'} label={label} />
      )}
      ListboxProps={{ style: { maxHeight: 250 }, onScroll: onScroll }}
      isOptionEqualToValue={(option, val) => isEqual(option.value, val.value)}
      filterOptions={(options, state) => filterOptions(options, state, projects)}
    />
  )
}

export default ProjectSelector
